import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

function CheckoutSupplierModal({ screen, currentUser, customers, onClose, onSuccess }) {
  const [isReimposing, setIsReimposing] = useState(false);
  const [supplierReason, setSupplierReason] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [artworkFile, setArtworkFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCustomerChange = (e) => {
    const value = e.target.value;
    setSelectedCustomerId(value);
    const customer = customers?.find(c => c.id === value);
    if (customer) {
      setNewCustomerName(customer.name);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        return;
      }
      setArtworkFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isReimposing) {
      // Validate re-impose fields
      if (!newCustomerName || !newItemDescription) {
        setError('Customer name and item description are required for re-imposing');
        return;
      }
      if (!artworkFile) {
        setError('Please upload new artwork PDF for re-imposing');
        return;
      }
    } else {
      // Validate regular supplier checkout
      if (!supplierReason.trim()) {
        setError('Please provide a reason for sending to supplier');
        return;
      }
    }

    setLoading(true);

    try {
      if (isReimposing) {
        // Handle re-impose workflow
        const formDataToSend = new FormData();
        formDataToSend.append('newCustomerName', newCustomerName);
        formDataToSend.append('newItemDescription', newItemDescription);
        formDataToSend.append('newArtworkPdf', artworkFile);
        formDataToSend.append('checkedOutBy', currentUser?.id || 'system');

        const response = await fetch(`${API_URL}/silkscreens/${screen.id}/reimpose-and-checkout`, {
          method: 'POST',
          body: formDataToSend
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to reimpose and checkout screen');
        }
      } else {
        // Handle regular supplier checkout
        const response = await fetch(`${API_URL}/silkscreens/${screen.id}/checkout-supplier`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            supplierReason: supplierReason,
            checkedOutBy: currentUser?.id || 'system'
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to checkout screen');
        }
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
      }}>
        <h2 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: '#FF9800',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          🚚 Checkout to Supplier
        </h2>

        <div style={{
          background: '#fff3e0',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '24px',
          fontSize: '0.9rem',
          color: '#e65100'
        }}>
          <strong>{screen.screenCode}</strong> - {screen.currentCustomerName}
          <br />
          {screen.currentItemDescription}
        </div>

        {error && (
          <div style={{
            background: '#ffebee',
            color: '#c62828',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '0.9rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Re-imposing Checkbox */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              padding: '12px',
              background: isReimposing ? '#f3e5f5' : '#f5f5f5',
              borderRadius: '8px',
              border: isReimposing ? '2px solid #9C27B0' : '2px solid transparent',
              transition: 'all 0.2s'
            }}>
              <input
                type="checkbox"
                checked={isReimposing}
                onChange={(e) => setIsReimposing(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontWeight: '600', color: '#333', fontSize: '0.95rem' }}>
                🔄 This is for re-imposing (changing customer/artwork)
              </span>
            </label>
          </div>

          {isReimposing ? (
            // Re-imposing fields
            <>
              <div style={{
                background: '#fff3e0',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '0.85rem',
                color: '#e65100'
              }}>
                ⚠️ <strong>Note:</strong> Re-imposing will move the current artwork to history and replace it with new artwork.
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#333',
                  fontSize: '0.95rem'
                }}>
                  New Customer *
                </label>
                <select
                  value={selectedCustomerId}
                  onChange={handleCustomerChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s',
                    cursor: 'pointer'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#9C27B0'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                >
                  <option value="">Select a customer...</option>
                  {customers && customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#333',
                  fontSize: '0.95rem'
                }}>
                  New Item Description *
                </label>
                <textarea
                  value={newItemDescription}
                  onChange={(e) => setNewItemDescription(e.target.value)}
                  placeholder="e.g., New Logo Design 2026"
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#9C27B0'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#333',
                  fontSize: '0.95rem'
                }}>
                  New Artwork PDF (Max 4MB) *
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px dashed #9C27B0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    background: '#fafafa'
                  }}
                />
                {artworkFile && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: '#e8f5e9',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    color: '#2e7d32'
                  }}>
                    ✓ {artworkFile.name} ({(artworkFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>
            </>
          ) : (
            // Regular supplier checkout fields
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#333',
                fontSize: '0.95rem'
              }}>
                Reason for Supplier Checkout *
              </label>
              <select
                value={supplierReason}
                onChange={(e) => setSupplierReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s',
                  cursor: 'pointer'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FF9800'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              >
                <option value="">Select a reason...</option>
                <option value="Physical repair">Physical repair</option>
                <option value="Re-impose same image">Re-impose same image</option>
                <option value="Layout change">Layout change</option>
                <option value="Cleaning">Cleaning</option>
                <option value="Other">Other</option>
              </select>
              <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                Select what needs to be done by the supplier
              </div>
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: '#f5f5f5',
                color: '#666',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#e0e0e0')}
              onMouseLeave={(e) => e.target.style.background = '#f5f5f5'}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: loading ? '#b0bec5' : '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = '#FB8C00')}
              onMouseLeave={(e) => !loading && (e.target.style.background = '#FF9800')}
            >
              {loading ? (isReimposing ? 'Re-imposing...' : 'Checking Out...') : (isReimposing ? 'Re-impose & Send to Supplier' : 'Checkout to Supplier')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CheckoutSupplierModal;
